import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { useQueryTableStore } from "@/components/query-table/stores/onlySelectedViewStore";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { ContractorsPlannedHoursWizardService } from "@/services/contractorsPlannedHoursWizardService";
import { SuccessFetchListResponse } from "@/services/api";
import { dataTableOnlySelectedViewModes } from "@/types/dataTableSettings";
import { DisplayContractorsPlannedHoursWizardAllocation } from "@/types/contractorsPlannedHoursWizard";
import { QueryKey } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";

/**
 * In `"all"` mode the query key follows filters/sorting (server). In `"selected"`
 * mode the key is frozen so UI filter/sort changes only affect the local
 * only-selected branch of `List`, not the API. The query stays subscribed so
 * `data` remains available for both views.
 *
 * "All" and "Selected" use separate filter/sort storage keys; the list `name` on
 * `List` stays the allocations table id so column layout is shared.
 */
export const useContractorsPlannedHoursWizardQuery = (tableName: string) => {
  const allocationView = useQueryTableStore(
    (s) => s.modesByTable[tableName] ?? dataTableOnlySelectedViewModes.all,
  );
  const optionsAll =
    useCreateDataTableOptions<DisplayContractorsPlannedHoursWizardAllocation>(
      tableName,
    );
  const optionsSelected =
    useCreateDataTableOptions<DisplayContractorsPlannedHoursWizardAllocation>(
      "contractorsPlannedHoursWizardAllocationsSelected",
    );
  const options =
    allocationView === dataTableOnlySelectedViewModes.all
      ? optionsAll
      : optionsSelected;

  const dynamicQueryKey = useDynamicQueryParams(tableName, optionsAll);
  const frozenServerQueryKeyRef = useRef<QueryKey>(dynamicQueryKey);
  if (allocationView === dataTableOnlySelectedViewModes.all) {
    frozenServerQueryKeyRef.current = dynamicQueryKey;
  }
  const queryKey =
    allocationView === dataTableOnlySelectedViewModes.all
      ? dynamicQueryKey
      : frozenServerQueryKeyRef.current;

  const queryFn = useCallback(
    ({ pageParam }: { pageParam: string | undefined }) =>
      ContractorsPlannedHoursWizardService.getList({
        lastItem: pageParam,
        filters: optionsAll.filters,
        sorting: optionsAll.sorting[0],
      }),
    [optionsAll.filters, optionsAll.sorting],
  );

  const getNextPageParam = useCallback(
    (
      lastPage: SuccessFetchListResponse<DisplayContractorsPlannedHoursWizardAllocation>,
    ) => {
      if (allocationView === dataTableOnlySelectedViewModes.selected)
        return undefined;
      return lastPage.last_item;
    },
    [allocationView],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam: undefined,
    getNextPageParam,
    staleTime:
      allocationView === dataTableOnlySelectedViewModes.selected ? Infinity : 0,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  return { query, options };
};
